import axios, { AxiosError, AxiosRequestConfig } from "axios";

const baseURL =
  import.meta.env.VITE_API_BASE_URL ?? "/api/v1";

export const api = axios.create({
  baseURL,
  withCredentials: true, // send refresh cookie
});

let accessToken: string | null = null;
let refreshing: Promise<string | null> | null = null;
const subscribers: ((token: string | null) => void)[] = [];

export function setAccessToken(t: string | null) {
  accessToken = t;
}

export function getAccessToken() {
  return accessToken;
}

async function refreshAccessToken(): Promise<string | null> {
  if (refreshing) return refreshing;
  refreshing = api
    .post<{ access_token: string }>("/auth/refresh")
    .then((r) => {
      accessToken = r.data.access_token;
      subscribers.forEach((cb) => cb(accessToken));
      subscribers.length = 0;
      return accessToken;
    })
    .catch(() => {
      accessToken = null;
      subscribers.forEach((cb) => cb(null));
      subscribers.length = 0;
      return null;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

api.interceptors.request.use((cfg) => {
  if (accessToken && cfg.headers) {
    cfg.headers.Authorization = `Bearer ${accessToken}`;
  }
  return cfg;
});

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const original = err.config as AxiosRequestConfig & { _retried?: boolean };
    if (
      err.response?.status === 401 &&
      original &&
      !original._retried &&
      !original.url?.endsWith("/auth/refresh") &&
      !original.url?.endsWith("/auth/login")
    ) {
      original._retried = true;
      const newToken = await refreshAccessToken();
      if (newToken) {
        original.headers = { ...(original.headers || {}), Authorization: `Bearer ${newToken}` };
        return api.request(original);
      }
    }
    throw err;
  },
);
